import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useErisInformationStore } from 'src/api/eris/store/ErisInformationStore';
import { useErisUserStore } from 'src/api/eris/store/ErisUserStore';
import { ERIS_Screen } from 'src/ui/eris/router/ErisScreen';
import { erisNavigate } from 'src/ui/eris/router/ErisScreensRegistration';
import { formatUnixSeconds } from 'src/common/date/customDate';
import useDisasterService from 'src/api/eris/service/ErisDisasterService';
import useGroupService from 'src/api/eris/service/ErisInformationService';
import Win95Button from '../../../common/button/Win95Button';
import RightTriangleIcon from '../../../common/icons/RightTriangleIcon';

const ErisHistoryComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useErisUserStore();
  const { getPreviousDisaster } = useDisasterService();
  const { previousDisasterUpdate, patchStatusPagination } = useErisInformationStore();
  const { prepareGroupSummaryCsvDownload, prepareGroupStatusCsvDownload } = useGroupService();

  useEffect(() => {
    getPreviousDisaster();
  }, [getPreviousDisaster]);

  const downloadFile = useCallback((blob: Blob | null, filename: string) => {
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, []);

  const handleExport = async (type: 'status' | 'summary') => {
    if (!previousDisasterUpdate) {
      alert('No History Found');
      return;
    }

    try {
      patchStatusPagination({
        searchParams: {
          userId: user?.id ?? '',
          groupName: null,
        },
      });

      const downloadOptions = {
        latest: false,
        disaster: null,
      };

      const { blob, filename } =
        type === 'status'
          ? await prepareGroupStatusCsvDownload(downloadOptions)
          : await prepareGroupSummaryCsvDownload(downloadOptions);

      downloadFile(blob, filename);
    } catch (err) {
      console.error(`Download failed`, err);
      alert('Download failed');
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <header>
        <div className="flex items-center gap-2">
          <RightTriangleIcon />
          <button
            className="text-xl font-bold hover:text-blue-600"
            onClick={() => erisNavigate(navigate, ERIS_Screen.Main)}
          >
            {t('eris.search.home')}
          </button>
        </div>
        <div className="mt-1 h-1 w-full bg-gradient-to-r from-blue-400 to-blue-700" />
      </header>

      {/* Title Section */}
      <div className="flex items-center justify-between px-1">
        <h2 className="text-2xl font-bold">{t('eris.history.previousRecoveryInformation')}</h2>
        <span className="text-sm">
          {t('eris.search.lastUpdate')}: {formatUnixSeconds(previousDisasterUpdate?.lastUpdateDate)}
        </span>
      </div>

      {/* Info & Buttons Section */}
      <div className="flex flex-col gap-8 ps-6">
        <div className="flex items-center gap-5">
          <label className="w-36 text-right font-medium">{t('eris.search.supportStartTime')}</label>
          <div>
            {previousDisasterUpdate?.startDate && previousDisasterUpdate?.stopDate && (
              <span>
                {formatUnixSeconds(previousDisasterUpdate.startDate)}
                {` [${t('eris.search.recoveryComplete')}]`}
              </span>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <Win95Button
            title={t('eris.history.supportStatusList')}
            onClick={() => handleExport('status')}
          />
          <Win95Button
            title={t('eris.history.supportStatusTotal')}
            onClick={() => handleExport('summary')}
          />
        </div>
      </div>

      <hr className="mt-3 border-gray-400" />
    </div>
  );
};

export default ErisHistoryComponent;
